import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Input, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import Api from '../../util/api';
import Loading from '../../shared/components/custom/Loading';
import FormCard from '../../shared/components/custom/FormCard';
import { formatAmount } from '../transactions/connect';
import ClubMonthDetail from './ClubMonthDetail';
import {
  balanceText,
  clubsOverMonths,
  errorText,
  lastMonth,
  MAX_MONTHS,
  monthsBetween,
  p,
  shiftMonth,
  STATUS_COLORS,
  paymentFeeText,
  totalsByCurrency,
} from './accounting';

// Every club's auctions in one month or several: sold, how it was paid,
// what we hold, the cut and the balance (pay out, or invoice), added up
// over the months. A club opens below the table with its sales and what to
// do with each month.
const AccountingMonths = ({ clubNames }) => {
  const [from, setFrom] = useState(lastMonth);
  const [to, setTo] = useState(lastMonth);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);
  const periods = monthsBetween(from, to);

  useEffect(() => {
    let current = true;
    const fetchMonths = async () => {
      setLoading(true);
      try {
        const months = await Promise.all(
          periods.map((period) => Api.accounting.getMonth(period))
        );
        if (current) {
          setRows(clubsOverMonths(periods, months));
        }
      } catch (err) {
        toast.error(errorText(err, 'loadERROR'));
      } finally {
        if (current) {
          setLoading(false);
        }
      }
    };
    fetchMonths();
    // Other months win over a slower earlier request
    return () => {
      current = false;
    };
    // periods follows from and to
    // eslint-disable-next-line
  }, [from, to, reload]);

  const clubName = (row) =>
    clubNames[`${row.teamId}`] || row.clubName || `#${row.teamId}`;

  // The range moves with either end: never backwards, at most MAX_MONTHS
  const changeFrom = (value) => {
    if (!value) return;
    const last = shiftMonth(value, MAX_MONTHS - 1);
    setFrom(value);
    if (value > to) {
      setTo(value);
    } else if (to > last) {
      setTo(last);
    }
    setSelected(null);
  };
  const changeTo = (value) => {
    if (!value) return;
    const first = shiftMonth(value, -(MAX_MONTHS - 1));
    setTo(value);
    if (value < from) {
      setFrom(value);
    } else if (from < first) {
      setFrom(first);
    }
    setSelected(null);
  };

  const amountCells = (values, currency) => (
    <>
      <td className="text-right">{values.sales}</td>
      <td className="text-right">{formatAmount(values.sold, currency)}</td>
      <td className="text-right">
        {formatAmount(values.paidInApp, currency)}
        {values.connect > 0 && (
          <small className="d-block text-muted">
            {p('viaConnect', {
              amount: formatAmount(values.connect, currency),
            })}
          </small>
        )}
      </td>
      <td className="text-right">
        {formatAmount(values.paidOutside, currency)}
      </td>
      <td className="text-right">{formatAmount(values.unpaid, currency)}</td>
      <td className="text-right">{formatAmount(values.held, currency)}</td>
      <td className="text-right">{formatAmount(values.cutOwed, currency)}</td>
      <td className="text-right">
        {formatAmount(values.cutCollected, currency)}
      </td>
      <td className="text-right text-muted">
        {paymentFeeText(values)}
        {values.paymentFeeMissing > 0 && (
          <small className="d-block">
            {p('paymentFeeMissing', { count: values.paymentFeeMissing })}
          </small>
        )}
      </td>
      <td className="text-right">
        {formatAmount(values.adjustments, currency)}
      </td>
      <td className="text-right bold">
        {balanceText(values.balance, currency)}
      </td>
    </>
  );

  // One month: its status. Several: how many months are in each
  const statusCell = (months) => {
    if (periods.length === 1) {
      return (
        <Badge color={STATUS_COLORS[months[0].status]}>
          {p(`status-${months[0].status}`)}
        </Badge>
      );
    }
    const counts = months.reduce(
      (sum, month) => ({
        ...sum,
        [month.status]: (sum[month.status] || 0) + 1,
      }),
      {}
    );
    return ['draft', 'approved', 'paid', 'invoiced']
      .filter((status) => counts[status])
      .map((status) => (
        <Badge key={status} color={STATUS_COLORS[status]} className="mr-1 mb-1">
          {p('statusCount', {
            status: p(`status-${status}`),
            count: counts[status],
          })}
        </Badge>
      ));
  };

  return (
    <>
      <Loading loading={loading} />
      <p className="mb-3">{p('intro')}</p>
      <div className="d-flex flex-wrap align-items-center mb-3">
        <label htmlFor="accounting-from" className="mb-0 mr-2">
          {p('fromMonth')}
        </label>
        <Input
          id="accounting-from"
          type="month"
          bsSize="sm"
          className="mr-3"
          style={{ maxWidth: 180 }}
          value={from}
          onChange={(e) => changeFrom(e.target.value)}
        />
        <label htmlFor="accounting-to" className="mb-0 mr-2">
          {p('toMonth')}
        </label>
        <Input
          id="accounting-to"
          type="month"
          bsSize="sm"
          className="mr-3"
          style={{ maxWidth: 180 }}
          value={to}
          onChange={(e) => changeTo(e.target.value)}
        />
        <small className="text-muted">
          {p('monthsShown', { count: periods.length, max: MAX_MONTHS })}
        </small>
      </div>
      {rows && !rows.length && (
        <p className="text-muted">
          {periods.length === 1 ? p('empty') : p('emptyRange')}
        </p>
      )}
      {rows?.length > 0 && (
        <FormCard bodyClassName="form-card__body--flush" className="mb-3">
          <Table responsive striped className="table--roomy table--stack mb-0">
            <thead>
              <tr>
                <th>{p('club')}</th>
                <th className="text-right">{p('sales')}</th>
                <th className="text-right">{p('sold')}</th>
                <th className="text-right">{p('paidInApp')}</th>
                <th className="text-right">{p('paidOutside')}</th>
                <th className="text-right">{p('unpaid')}</th>
                <th className="text-right">{p('held')}</th>
                <th className="text-right">{p('cutOwed')}</th>
                <th className="text-right">{p('cutCollected')}</th>
                <th className="text-right" title={p('paymentFeeHelp')}>
                  {p('paymentFee')}
                </th>
                <th className="text-right">{p('adjustments')}</th>
                <th className="text-right">{p('balance')}</th>
                <th>{p('status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.teamId}>
                  <td>
                    {clubName(row)}
                    {row.values.missingRate > 0 && (
                      <small className="d-block text-danger">
                        {p('missingRate', { count: row.values.missingRate })}
                      </small>
                    )}
                  </td>
                  {amountCells(row.values, row.currency)}
                  <td>{statusCell(row.months)}</td>
                  <td>
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      className="mb-0"
                      onClick={() => setSelected(row.teamId)}
                    >
                      {p('details')}
                    </Button>
                  </td>
                </tr>
              ))}
              {totalsByCurrency(rows).map((total) => (
                <tr key={`total-${total.currency}`} className="bold">
                  <td>{p('total', { currency: total.currency })}</td>
                  {amountCells(total, total.currency)}
                  <td />
                  <td />
                </tr>
              ))}
            </tbody>
          </Table>
        </FormCard>
      )}
      {selected && (
        <ClubMonthDetail
          teamId={selected}
          periods={periods}
          clubName={clubNames[`${selected}`] || `#${selected}`}
          onClose={() => setSelected(null)}
          onChanged={() => setReload((count) => count + 1)}
        />
      )}
    </>
  );
};

AccountingMonths.propTypes = {
  clubNames: PropTypes.objectOf(PropTypes.string),
};

AccountingMonths.defaultProps = {
  clubNames: {},
};

export default AccountingMonths;
